import type {Comment as TComment} from '@/apiTypes/comment';
import type {RegistrationStatus} from '@/apiTypes/registration';
import {FC, useEffect, useState} from 'react';
import {Comment} from './comment/comment';
import {Organizer} from '@/apiTypes/event';
import {CommentComposer} from './commentComposer/commentComposer';
import {Button} from '../ui/button';
import {Link, useLocation} from '@tanstack/react-router';
import {useAuth} from '@/components/AuthProvider';
import {Typography} from '../ui/typography';
import {EventSection} from '../eventPage/eventSection';

interface CommentsCardProps {
  eventId: string;
  comments: TComment[];
  /** Separate from comments.length — unauthenticated users receive an empty
   *  comments array but a non-zero totalCount for the teaser prompt. */
  totalCount: number;
  isFetching: boolean;
  isOrganizerAdmin: boolean;
  registrationStatus: {status: RegistrationStatus} | null;
  organizer?: Organizer;
}
/**
 * Card displaying event comments with visibility gating and a composer for
 * authenticated users. Optimistically prepends new comments on submit.
 * Unauthenticated users see a teaser count with a prompt to log in.
 * Organizer updates are shown pinned above regular comments.
 */
export const CommentsCard: FC<CommentsCardProps> = ({
  eventId,
  comments: initialComments,
  totalCount: initialTotalCount,
  isFetching,
  isOrganizerAdmin,
  registrationStatus,
  organizer,
}) => {
  const {profile} = useAuth();
  const isAuthenticated = profile !== null;
  const currentProfileId = profile?.id ?? null;
  const [comments, setComments] = useState<TComment[]>(initialComments);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setComments(initialComments);
    setTotalCount(initialTotalCount);
  }, [initialComments, initialTotalCount]);

  if (isFetching && comments.length === 0) return null;
  if (totalCount === 0 && !isAuthenticated) return null;

  function handleDeleteComment(commentId: string) {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setTotalCount((prev) => prev - 1);
  }

  function handleNewComment(comment: TComment) {
    setComments((prev) => {
      if (comment.type === 'UPDATE') {
        // Prepend at the very front — updates are ordered newest-first.
        return [comment, ...prev];
      }
      // Regular comments go after all updates.
      const firstCommentIdx = prev.findIndex((c) => c.type === 'COMMENT');
      if (firstCommentIdx === -1) return [...prev, comment];
      return [
        ...prev.slice(0, firstCommentIdx),
        comment,
        ...prev.slice(firstCommentIdx),
      ];
    });
    setTotalCount((prev) => prev + 1);
  }

  const updates = comments.filter((c) => c.type === 'UPDATE');
  const regularComments = comments.filter((c) => c.type === 'COMMENT');

  const PREVIEW_COUNT = 4;
  const hasMore = regularComments.length > PREVIEW_COUNT;
  const visibleRegularComments = expanded
    ? regularComments
    : regularComments.slice(0, PREVIEW_COUNT);
  const remainingCount = regularComments.length - PREVIEW_COUNT;

  return (
    <EventSection label="Comments & Updates">
      {/* Comment composer for authenticated users */}
      {isAuthenticated && (
        <CommentComposer
          eventId={eventId}
          isOrganizerAdmin={isOrganizerAdmin}
          onSubmit={handleNewComment}
        />
      )}

      {/* Unauthenticated teaser */}
      {!isAuthenticated && (
        <div className="flex flex-col gap-3">
          <div>
            <Typography level="B2">
              See what riders are saying about this event.
            </Typography>

            <p className="text-sm text-gray-500">
              Sign up or log in to read comments and organizer updates.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/login"
              search={{
                mode: 'signup',
                redirect: `${location.pathname}${location.searchStr}${location.hash}`,
              }}
            >
              <Button size="sm">Sign Up</Button>
            </Link>
            <Link
              to="/login"
              search={{
                mode: 'login',
                redirect: `${location.pathname}${location.searchStr}${location.hash}`,
              }}
            >
              <Button variant="ghost" size="sm">
                Login
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Organizer updates section — always fully expanded */}
      {isAuthenticated && updates.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Organizer Updates
            </span>{' '}
            <span className="bg-primary-400 rounded-full px-2 py-0.5 text-white text-xs font-normal">
              {updates.length}
            </span>
            <hr className="flex-1 border-gray-200" />
          </div>
          <div className="space-y-3">
            {updates.map((commentItem) => (
              <Comment
                key={commentItem.id}
                comment={commentItem}
                organizer={organizer}
                canDelete={
                  commentItem.authorProfileId === currentProfileId ||
                  isOrganizerAdmin
                }
                onDelete={handleDeleteComment}
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular comment list */}
      {isAuthenticated && regularComments.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide gap-2 flex items-center">
              Comments{' '}
              <span className="bg-primary-400 rounded-full px-2 py-0.5 text-white text-xs font-normal">
                {totalCount}
              </span>
            </span>
            <hr className="flex-1 border-gray-200" />
          </div>

          <div id="comment-list" className="space-y-4">
            {visibleRegularComments.map((commentItem, index) => {
              const isLastVisible =
                !expanded && index === visibleRegularComments.length - 1;
              const shouldClip = isLastVisible && hasMore;
              const showRegisteredBadge =
                commentItem.authorProfileId === currentProfileId &&
                registrationStatus?.status === 'APPROVED';

              return (
                <Comment
                  key={commentItem.id}
                  comment={commentItem}
                  organizer={organizer}
                  showRegisteredBadge={showRegisteredBadge}
                  clipped={shouldClip}
                  canDelete={
                    commentItem.authorProfileId === currentProfileId ||
                    isOrganizerAdmin
                  }
                  onDelete={handleDeleteComment}
                />
              );
            })}
          </div>
        </>
      )}

      {/* No comments yet */}
      {isAuthenticated && comments.length === 0 && (
        <p className="text-sm text-gray-500">
          No comments yet. Be the first to comment.
        </p>
      )}

      {/* Expand / collapse toggle — applies to regular comments only */}
      {isAuthenticated && hasMore && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="comment-list"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-3 text-sm font-medium text-neutral-500 hover:text-primary-500 cursor-pointer"
        >
          {expanded
            ? 'Show less'
            : `Show ${remainingCount} more comment${remainingCount !== 1 ? 's' : ''}`}
        </button>
      )}
    </EventSection>
  );
};
